import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Wifi, WifiOff, RefreshCw, Users, TrendingUp, Server, Eye, Router, Signal, ChevronLeft, ChevronRight, Copy, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Debounce helper
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const MikrotikMonitoring = () => {
  const navigate = useNavigate();
  const { selectedBranch } = useApp();
  const [customers, setCustomers] = useState([]);
  const [onlineStatus, setOnlineStatus] = useState({});
  const [deviceStatus, setDeviceStatus] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    online: 0,
    offline: 0,
    isolated: 0,
    devicesOnline: 0,
    devicesOffline: 0
  });
  
  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Debounced search for server-side filtering
  const debouncedSearch = useDebounce(searchTerm, 500);

  // Fetch with server-side pagination
  const fetchUnifiedMonitoring = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build query params for server-side pagination
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString()
      });
      
      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }
      
      // Add branch filter if selected
      if (selectedBranch && selectedBranch !== 'all') {
        params.append('branch_id', selectedBranch);
      }
      
      // Fetch unified monitoring data with pagination
      const response = await axios.get(`${API_URL}/api/v2/monitoring/unified?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const unifiedData = response.data.data.customers;
        const statsData = response.data.data.stats;
        const pagination = response.data.data.pagination;
        
        // Filter customers with mikrotik_user OR with ACS devices
        const mikrotikCustomers = unifiedData.filter(c => 
          c.mikrotik_user || (c.devices && c.devices.length > 0)
        );
        setCustomers(mikrotikCustomers);
        
        // Build status maps
        const pppoeStatusMap = {};
        const deviceStatusMap = {};
        
        mikrotikCustomers.forEach(customer => {
          pppoeStatusMap[customer.customer_id] = customer.pppoe_status;
          deviceStatusMap[customer.customer_id] = {
            devices: customer.devices || [],
            status: customer.device_status || null
          };
        });
        
        setOnlineStatus(pppoeStatusMap);
        setDeviceStatus(deviceStatusMap);
        
        // Update stats from server response
        let devicesOnline = 0;
        let devicesOffline = 0;
        mikrotikCustomers.forEach(c => {
          if (c.device_status === 'online') devicesOnline++;
          if (c.device_status === 'offline') devicesOffline++;
        });
        
        setStats({
          total: statsData.total || 0,
          online: statsData.online || 0,
          offline: statsData.offline || 0,
          isolated: 0,
          devicesOnline,
          devicesOffline
        });
        
        // Update pagination from server
        if (pagination) {
          setTotalCount(pagination.total_count || 0);
          setTotalPages(pagination.total_pages || 0);
        }
      }
    } catch (error) {
      console.error('Error fetching unified monitoring:', error);
      toast.error('Gagal memuat data monitoring');
    } finally {
      setIsRefreshing(false);
    }
  }, [currentPage, itemsPerPage, debouncedSearch, selectedBranch]);

  // Fetch on mount and when pagination/search changes
  useEffect(() => {
    fetchUnifiedMonitoring();
  }, [fetchUnifiedMonitoring]);

  // Auto refresh DISABLED - System is PASSIVE
  // Data is updated by MikroTik webhooks (on-up/on-down scripts)
  // No polling to MikroTik - saves resources
  // useEffect removed to prevent any polling

  // Reset page when search or branch changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedBranch]);

  const handleManualRefresh = () => {
    fetchUnifiedMonitoring();
  };

  const formatUptime = (uptime) => {
    if (!uptime) return '-';
    return uptime;
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Calculate display range
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + customers.length, totalCount);

  // Generate page numbers
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 px-4 sm:px-6 py-4 space-y-4">
      {/* Header - Compact */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2 dark:text-slate-200">
            <Activity className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            Real-Time Monitoring
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Monitor status online customer & bandwidth
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Auto Refresh Toggle */}
          <label className="flex items-center gap-1.5 cursor-pointer text-xs">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600 dark:text-indigo-400"
            />
            <span className="text-slate-600 dark:text-slate-300">Auto</span>
          </label>
          
          {/* Manual Refresh Button */}
          <Button
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Stats Cards - Responsive: 2 cols mobile, 3 cols tablet, 6 cols desktop */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-indigo-50 to-purple-50">
          <CardContent className="p-2.5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-slate-500 leading-tight dark:text-slate-400">Total</p>
                <p className="text-lg font-bold text-indigo-600 leading-tight dark:text-indigo-400">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-2.5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Wifi className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-slate-500 leading-tight dark:text-slate-400">Online</p>
                <p className="text-lg font-bold text-green-600 leading-tight dark:text-green-400">{stats.online}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-red-50 to-rose-50">
          <CardContent className="p-2.5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <WifiOff className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-slate-500 leading-tight dark:text-slate-400">Offline</p>
                <p className="text-lg font-bold text-red-600 leading-tight dark:text-red-400">{stats.offline}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-orange-50 to-amber-50">
          <CardContent className="p-2.5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Server className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-slate-500 leading-tight dark:text-slate-400">Isolir</p>
                <p className="text-lg font-bold text-orange-600 leading-tight dark:text-orange-400">{stats.isolated}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-teal-50 to-cyan-50">
          <CardContent className="p-2.5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Router className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-slate-500 leading-tight dark:text-slate-400">CPE On</p>
                <p className="text-lg font-bold text-teal-600 leading-tight dark:text-teal-400">{stats.devicesOnline}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-gray-50 to-slate-100">
          <CardContent className="p-2.5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gray-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Router className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-slate-500 leading-tight dark:text-slate-400">CPE Off</p>
                <p className="text-lg font-bold text-gray-600 leading-tight dark:text-slate-300">{stats.devicesOffline}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customer List - Compact */}
      <Card className="border-0 shadow-md">
        <CardHeader className="border-b border-slate-100 py-3 px-4 dark:border-slate-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700 dark:text-slate-200">
              <Eye className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              Customer Status
            </CardTitle>
            
            {/* Search and Items per page - Compact */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Cari..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-2.5 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 w-32 dark:border-slate-700"
              />
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-700"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Info: Showing X of Y - Compact */}
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between text-xs dark:bg-slate-900 dark:border-slate-700">
            <span className="text-slate-600 dark:text-slate-300">
              {totalCount > 0 ? startIndex + 1 : 0} - {endIndex} dari {totalCount}
            </span>
            {isRefreshing && (
              <span className="text-indigo-600 flex items-center gap-1 dark:text-indigo-400">
                <RefreshCw className="w-3 h-3 animate-spin" />
                Loading...
              </span>
            )}
          </div>

          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 border-b border-slate-200 dark:bg-slate-800 dark:border-slate-700">
                <tr>
                  <th className="text-left py-2 px-2 sm:px-3 text-xs font-semibold text-slate-600 whitespace-nowrap sticky left-0 bg-slate-100 z-10 dark:text-slate-300 dark:bg-slate-800">Status</th>
                  <th className="text-left py-2 px-2 sm:px-3 text-xs font-semibold text-slate-600 whitespace-nowrap dark:text-slate-300">Customer</th>
                  <th className="text-left py-2 px-2 sm:px-3 text-xs font-semibold text-slate-600 whitespace-nowrap hidden sm:table-cell dark:text-slate-300">Username</th>
                  <th className="text-left py-2 px-2 sm:px-3 text-xs font-semibold text-slate-600 whitespace-nowrap hidden lg:table-cell dark:text-slate-300">CPE</th>
                  <th className="text-left py-2 px-2 sm:px-3 text-xs font-semibold text-slate-600 whitespace-nowrap dark:text-slate-300">IP</th>
                  <th className="text-left py-2 px-2 sm:px-3 text-xs font-semibold text-slate-600 whitespace-nowrap hidden lg:table-cell dark:text-slate-300">Uptime</th>
                  <th className="text-left py-2 px-2 sm:px-3 text-xs font-semibold text-slate-600 whitespace-nowrap hidden lg:table-cell dark:text-slate-300">↓ DL</th>
                  <th className="text-left py-2 px-2 sm:px-3 text-xs font-semibold text-slate-600 whitespace-nowrap hidden lg:table-cell dark:text-slate-300">↑ UL</th>
                  <th className="text-center py-2 px-1 sm:px-3 text-xs font-semibold text-slate-600 w-8 dark:text-slate-300"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {customers.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-6 text-slate-500 text-sm dark:text-slate-400">
                      {isRefreshing ? 'Memuat data...' : 'Tidak ada customer dengan MikroTik user'}
                    </td>
                  </tr>
                ) : (
                  customers.map((customer, index) => {
                    const status = onlineStatus[customer.customer_id] || {};
                    const isOnline = status.online || false;
                    const data = status.data || {};
                    
                    // Get IP and uptime from pppoe_status
                    const ipAddress = status.ip_address || data.ip_address || customer.ip_address || '-';
                    const uptime = status.uptime || data.uptime;
                    
                    const deviceInfo = deviceStatus[customer.customer_id] || {};
                    const devices = deviceInfo.devices || [];
                    const deviceOnline = deviceInfo.status === 'online';
                    
                    return (
                      <tr key={customer.customer_id} className={`hover:bg-indigo-50/50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                        <td className="py-1.5 px-2 sm:px-3 sticky left-0 bg-inherit z-10">
                          <div className="flex items-center gap-1">
                            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`}></div>
                            <span className={`text-[10px] sm:text-xs font-medium ${isOnline ? 'text-green-600' : 'text-slate-500'}`}>
                              {isOnline ? 'ON' : 'OFF'}
                            </span>
                          </div>
                        </td>
                        <td className="py-1.5 px-2 sm:px-3">
                          <p 
                            className="text-[11px] sm:text-xs font-medium text-slate-800 hover:text-indigo-600 cursor-pointer truncate max-w-[120px] sm:max-w-[150px] dark:text-slate-200"
                            onClick={() => navigate(`/app/customers/${customer.customer_id}`)}
                            title={customer.customer_name}
                          >
                            {customer.customer_name}
                          </p>
                          <p className="text-[10px] text-slate-400 truncate max-w-[120px] sm:max-w-[150px] dark:text-slate-500">
                            {customer.package}
                          </p>
                        </td>
                        <td className="py-1.5 px-2 sm:px-3 hidden sm:table-cell">
                          <code className="text-[10px] sm:text-[11px] bg-slate-100 px-1 sm:px-1.5 py-0.5 rounded text-slate-700 truncate max-w-[80px] sm:max-w-none block dark:bg-slate-800 dark:text-slate-200">{customer.mikrotik_user}</code>
                        </td>
                        <td className="py-1.5 px-2 sm:px-3 hidden lg:table-cell">
                          {devices.length > 0 ? (
                            <div className="flex items-center gap-1">
                              <Router size={12} className={deviceOnline ? 'text-teal-600' : 'text-gray-400'} />
                              <span className={`text-[10px] font-medium ${deviceOnline ? 'text-teal-600' : 'text-gray-500'}`}>
                                {deviceOnline ? 'ON' : 'OFF'}
                              </span>
                            </div>
                          ) : (
                            <span className="text-[10px] text-slate-400 dark:text-slate-500">-</span>
                          )}
                        </td>
                        <td className="py-1.5 px-1 sm:px-3">
                          {ipAddress && ipAddress !== '-' ? (
                            <div className="flex items-center gap-0.5 sm:gap-1">
                              <span className="text-[10px] sm:text-xs text-slate-600 font-mono truncate max-w-[90px] sm:max-w-none dark:text-slate-300">
                                {ipAddress}
                              </span>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(ipAddress);
                                  toast.success('IP disalin!');
                                }}
                                className="text-slate-400 hover:text-indigo-600 transition-colors p-0.5 flex-shrink-0 dark:text-slate-500"
                                title="Salin IP"
                              >
                                <Copy size={12} />
                              </button>
                              <button
                                onClick={() => window.open(`http://${ipAddress}`, '_blank')}
                                className="text-green-500 hover:text-green-600 transition-colors p-0.5 flex-shrink-0"
                                title="Buka di browser"
                              >
                                <ExternalLink size={12} />
                              </button>
                            </div>
                          ) : (
                            <span className="text-[10px] sm:text-xs text-slate-400 dark:text-slate-500">-</span>
                          )}
                        </td>
                        <td className="py-1.5 px-2 sm:px-3 hidden lg:table-cell">
                          <span className="text-xs text-slate-600 dark:text-slate-300">
                            {isOnline ? formatUptime(uptime) : '-'}
                          </span>
                        </td>
                        <td className="py-1.5 px-2 sm:px-3 hidden lg:table-cell">
                          <span className="text-xs text-green-600 font-medium dark:text-green-400">
                            {isOnline ? formatBytes(data.bytes_in) : '-'}
                          </span>
                        </td>
                        <td className="py-1.5 px-2 sm:px-3 hidden lg:table-cell">
                          <span className="text-xs text-blue-600 font-medium dark:text-blue-400">
                            {isOnline ? formatBytes(data.bytes_out) : '-'}
                          </span>
                        </td>
                        <td className="py-1.5 px-1 sm:px-3 text-center">
                          <button
                            onClick={() => navigate(`/app/customers/${customer.customer_id}`)}
                            className="text-slate-400 hover:text-indigo-600 transition-colors dark:text-slate-500"
                            title="View detail"
                          >
                            <Eye size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination - Compact */}
          {totalPages > 1 && (
            <div className="px-3 py-2 border-t border-slate-100 flex items-center justify-between text-xs dark:border-slate-700">
              <span className="text-slate-500 dark:text-slate-400">
                Hal {currentPage}/{totalPages}
              </span>
              
              <div className="flex items-center gap-0.5">
                {/* Previous */}
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-2 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed dark:border-slate-700 dark:hover:bg-slate-700/50"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                
                {/* Page Numbers */}
                {getPageNumbers().map((page, idx) => (
                  page === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-1.5 text-slate-400 dark:text-slate-500">...</span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`min-w-[28px] py-1 rounded text-xs ${
                        currentPage === page 
                          ? 'bg-indigo-600 text-white font-medium' 
                          : 'border border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {page}
                    </button>
                  )
                ))}
                
                {/* Next */}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed dark:border-slate-700 dark:hover:bg-slate-700/50"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MikrotikMonitoring;
